/**
 * Lightweight per-document event ring buffer for sync pipeline diagnostics.
 *
 * Events are keyed by room key (e.g. "doc:abc123") so both crdtServiceWS and
 * docsCRDTService can log to the same timeline without needing to strip prefixes.
 * Each key retains at most MAX_EVENTS entries; older entries are dropped (FIFO).
 *
 * This module is a singleton so it can be imported anywhere without circular deps.
 * Memory cost: ~200 bytes per event × 60 events × open docs — negligible.
 */

const MAX_EVENTS = 60;

export interface DocEvent {
  ts: number;        // Date.now()
  event: string;     // Short category label, e.g. "ws-connected"
  detail?: string;   // Optional human-readable context
}

class DocEventLog {
  private readonly events = new Map<string, DocEvent[]>();

  log(key: string, event: string, detail?: string): void {
    if (!key) return;
    let arr = this.events.get(key);
    if (!arr) {
      arr = [];
      this.events.set(key, arr);
    }
    arr.push({ ts: Date.now(), event, detail });
    if (arr.length > MAX_EVENTS) arr.shift();
  }

  getEvents(key: string): DocEvent[] {
    return this.events.get(key) ?? [];
  }

  clear(key: string): void {
    this.events.delete(key);
  }

  /** Format events for the debug report. Returns an empty string if no events. */
  format(key: string): string {
    const evs = this.getEvents(key);
    if (evs.length === 0) return '  (no events recorded for this session)';
    return evs
      .map(e => {
        const d = new Date(e.ts);
        const hh = String(d.getUTCHours()).padStart(2, '0');
        const mm = String(d.getUTCMinutes()).padStart(2, '0');
        const ss = String(d.getUTCSeconds()).padStart(2, '0');
        const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
        const label = e.event.padEnd(22);
        return `  ${hh}:${mm}:${ss}.${ms}  [${label}]  ${e.detail ?? ''}`;
      })
      .join('\n');
  }
}

export const docEventLog = new DocEventLog();
